import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  SidePanel,
  type BadgeVariant,
} from '@silverfalls/sterling';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import {
  approvePaymentRun,
  declinePaymentRun,
  executePaymentRun,
  fetchPaymentRun,
  markPaymentCleared,
  type PaymentRunPaymentRow,
  type PaymentRunRow,
  type PaymentRunState,
} from '../api/p2p.js';
import { formatDateTime, formatRelativeDate } from '../lib/format.js';

const REQUESTING_USER = 'u-ui-skeleton';

export function PaymentRunDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const toast = useToast();
  const [run, setRun] = useState<PaymentRunRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmExecute, setConfirmExecute] = useState(false);
  // Per-payment clearing — tracked by paymentRef so multiple buttons
  // can have independent loading states.
  const [clearing, setClearing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchPaymentRun(ref);
      setRun(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const onMarkCleared = useCallback(
    async (paymentRef: string) => {
      setClearing(paymentRef);
      try {
        const result = await markPaymentCleared({
          paymentRef,
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'MarkPaymentClearedFailure') {
          toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
        } else if (result.runReconciled) {
          toast.push('Last payment cleared — run is RECONCILED.', 'success');
          await load();
        } else {
          toast.push('Payment marked CLEARED.', 'success');
          await load();
        }
      } catch (err) {
        toast.push(err instanceof Error ? err.message : String(err), 'error');
      } finally {
        setClearing(null);
      }
    },
    [load, toast],
  );

  const onExecute = useCallback(async () => {
    if (!run) return;
    setExecuting(true);
    try {
      const result = await executePaymentRun({
        runRef: run.runRef,
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'ExecutePaymentRunFailure') {
        toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
      } else {
        toast.push(
          `Executed — ${result.executedPaymentCount} payment(s) marked EXECUTED with stub refs.`,
          'success',
        );
        await load();
      }
    } catch (err) {
      toast.push(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setExecuting(false);
      setConfirmExecute(false);
    }
  }, [run, load, toast]);

  const onApprove = useCallback(async () => {
    if (!run) return;
    setApproving(true);
    try {
      const result = await approvePaymentRun({
        runRef: run.runRef,
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'ApprovePaymentRunFailure') {
        toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
      } else if (result.requiresSecondApproval) {
        toast.push(
          'First approval recorded — a second approver (different operator) is required.',
          'info',
        );
        await load();
      } else {
        toast.push('Payment run approved.', 'success');
        await load();
      }
    } catch (err) {
      toast.push(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setApproving(false);
      setConfirmApprove(false);
    }
  }, [run, load, toast]);

  if (loading) return <div className="t-body-sm">Loading payment run…</div>;
  if (error) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load run: {error}
      </div>
    );
  }
  if (!run) {
    return (
      <div className="empty-state">
        <p>Run not found: {ref}</p>
        <p>
          <Link to="/payment-runs">Back to payment runs</Link>
        </p>
      </div>
    );
  }

  const canApprove = run.state === 'PROPOSED';
  const canDecline = run.state === 'PROPOSED';
  const canExecute = run.state === 'APPROVED';
  // Did the run already collect a first approval? If so, the visible "Approve"
  // surfaces as "Approve (2nd)".
  const isAwaitingSecond = run.state === 'PROPOSED' && run.approvedBy !== null;

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/payment-runs" className="back-link">
            ← Payment runs
          </Link>
          <h2 className="mono">{run.runRef.slice(0, 24)}…</h2>
          <div className="entity-ref mono">{run.runRef}</div>
          <div className="entity-summary">
            <span className="entity-summary-part">
              <Badge variant={runStateVariant(run.state)} dot={false}>
                {run.state}
              </Badge>
            </span>
            <span className="entity-summary-part">{run.opcoRef}</span>
            <span className="entity-summary-part mono">
              {run.paymentCount} payment{run.paymentCount === 1 ? '' : 's'}
            </span>
            <span className="entity-summary-part mono">
              {run.netTotal} {run.currencyCode}
            </span>
            <span className="entity-summary-part mono">
              {formatRelativeDate(run.scheduleDate)}
            </span>
          </div>
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            {canApprove && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setConfirmApprove(true)}
                disabled={approving}
                loading={approving}
              >
                {isAwaitingSecond ? 'Approve (2nd)' : 'Approve'}
              </Button>
            )}
            {canDecline && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeclineOpen(true)}
                disabled={approving}
              >
                Decline
              </Button>
            )}
            {canExecute && (
              <Button
                variant="primary"
                size="sm"
                icon="arrow-right"
                onClick={() => setConfirmExecute(true)}
                disabled={executing}
                loading={executing}
                title="Stub execution — marks payments EXECUTED with synthetic refs (no real bank yet)"
              >
                Execute
              </Button>
            )}
          </div>
        </div>
      </div>

      {isAwaitingSecond && (
        <div className="alert alert-warn" role="status">
          <strong>Awaiting second approval.</strong> First approved by{' '}
          <span className="mono">{run.approvedBy}</span> at{' '}
          <span className="mono">{formatDateTime(run.approvedAt ?? '')}</span>. The run
          stays in PROPOSED until a different operator approves it.
        </div>
      )}
      {run.state === 'DECLINED' && (
        <div className="alert alert-warn" role="status">
          <strong>Declined.</strong>{' '}
          {run.declinedBy && (
            <>
              by <span className="mono">{run.declinedBy}</span>
            </>
          )}
          {run.declinedAt && (
            <>
              {' '}at <span className="mono">{formatDateTime(run.declinedAt)}</span>
            </>
          )}
          {run.declinedReason && <> — {run.declinedReason}</>}
        </div>
      )}

      <div className="detail-grid">
        <div className="detail-card">
          <h3 className="detail-card-title">Run header</h3>
          <div className="detail-rows">
            <DetailRow label="Schedule date" value={formatDateTime(run.scheduleDate)} mono />
            <DetailRow label="Lookahead" value={`${run.lookaheadDays} days`} />
            <DetailRow label="Gross total" value={`${run.grossTotal} ${run.currencyCode}`} mono />
            <DetailRow label="Discount total" value={`${run.discountTotal} ${run.currencyCode}`} mono />
            <DetailRow label="Credit total" value={`${run.creditTotal} ${run.currencyCode}`} mono />
            <DetailRow label="Net total" value={`${run.netTotal} ${run.currencyCode}`} mono />
            <DetailRow label="Created by" value={run.createdBy} mono />
            <DetailRow label="Created at" value={formatDateTime(run.createdAt)} mono />
            {run.approvedAt && (
              <DetailRow
                label="First approval"
                value={`${run.approvedBy} · ${formatDateTime(run.approvedAt)}`}
                mono
              />
            )}
            {run.secondApprovedAt && (
              <DetailRow
                label="Second approval"
                value={`${run.secondApprovedBy} · ${formatDateTime(run.secondApprovedAt)}`}
                mono
              />
            )}
          </div>
        </div>
      </div>

      <div className="po-lines">
        <div className="po-lines-header">
          <span className="form-label">Payments ({run.payments.length})</span>
        </div>
        <table className="po-lines-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Vendor</th>
              <th>Method</th>
              <th>State</th>
              <th>Gross</th>
              <th>Discount</th>
              <th>Credit</th>
              <th>Net</th>
              <th>Applications</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {run.payments.map((p, idx) => (
              <tr key={p.paymentRef}>
                <td className="mono">{idx + 1}</td>
                <td>
                  <Link to={`/vendors/${p.vendorRef}`} className="mono">
                    {p.vendorRef.slice(0, 16)}…
                  </Link>
                </td>
                <td>{p.paymentMethod}</td>
                <td>
                  <Badge variant={paymentStateVariant(p.state)} dot={false}>
                    {p.state}
                  </Badge>
                </td>
                <td className="mono">{p.grossAmount}</td>
                <td className="mono">{p.discountAmount}</td>
                <td className="mono">{p.creditAmount}</td>
                <td className="mono">{p.netAmount}</td>
                <td>{renderApplications(p)}</td>
                <td>
                  {p.state === 'EXECUTED' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void onMarkCleared(p.paymentRef)}
                      loading={clearing === p.paymentRef}
                      disabled={clearing !== null && clearing !== p.paymentRef}
                      title="Mark this payment CLEARED (manual reconciliation)"
                    >
                      Mark cleared
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={confirmApprove}
        title={isAwaitingSecond ? 'Confirm second approval' : 'Approve payment run'}
        variant="default"
        confirmLabel={isAwaitingSecond ? 'Confirm second approval' : 'Approve'}
        onCancel={() => setConfirmApprove(false)}
        onConfirm={() => void onApprove()}
      >
        {isAwaitingSecond
          ? `Confirming the second approval will mark the run APPROVED and cascade its ${run.paymentCount} payment(s) to APPROVED state.`
          : `Approving will mark this run APPROVED${
              // We don't know the OPCO's threshold from this screen; the gadget
              // computes it server-side. The toast on response tells the user
              // whether a second approval is now required.
              ''
            } (or capture a first approval when the OPCO's dual-approval threshold applies).`}
      </Modal>

      <Modal
        open={confirmExecute}
        title="Execute payment run"
        variant="default"
        confirmLabel="Execute (stub)"
        onCancel={() => setConfirmExecute(false)}
        onConfirm={() => void onExecute()}
      >
        Executing marks {run.paymentCount} payment(s) as EXECUTED with synthetic
        external refs. Real bank / ACH / wire integration isn't wired yet — this
        is the stub path that completes the state machine.
      </Modal>

      {declineOpen && (
        <DeclineRunDialog
          run={run}
          onClose={() => setDeclineOpen(false)}
          onDone={() => {
            setDeclineOpen(false);
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

function renderApplications(p: PaymentRunPaymentRow): JSX.Element {
  if (p.applications.length === 0) return <span className="cell-secondary">—</span>;
  const inv = p.applications.filter((a) => a.linkKind === 'INVOICE_APPLIED').length;
  const credit = p.applications.filter((a) => a.linkKind === 'CREDIT_APPLIED').length;
  const parts: string[] = [];
  if (inv > 0) parts.push(`${inv} invoice${inv === 1 ? '' : 's'}`);
  if (credit > 0) parts.push(`${credit} credit${credit === 1 ? '' : 's'}`);
  return <span>{parts.join(' · ')}</span>;
}

function paymentStateVariant(
  state: PaymentRunPaymentRow['state'],
): BadgeVariant {
  switch (state) {
    case 'PROPOSED':
      return 'info';
    case 'APPROVED':
    case 'EXECUTED':
    case 'CLEARED':
      return 'success';
    case 'EXECUTING':
      return 'warning';
    case 'FAILED':
    case 'STOPPED':
      return 'danger';
    case 'EXCLUDED':
      return 'neutral';
  }
}

function runStateVariant(state: PaymentRunState): BadgeVariant {
  switch (state) {
    case 'PROPOSED':
      return 'info';
    case 'APPROVED':
    case 'EXECUTED':
    case 'RECONCILED':
      return 'success';
    case 'EXECUTING':
    case 'PARTIALLY_EXECUTED':
      return 'warning';
    case 'DECLINED':
      return 'neutral';
  }
}

interface DeclineRunDialogProps {
  readonly run: PaymentRunRow;
  readonly onClose: () => void;
  readonly onDone: () => void;
  readonly onError: (message: string) => void;
}

function DeclineRunDialog({
  run,
  onClose,
  onDone,
  onError,
}: DeclineRunDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (reason.trim() === '') {
      setError('Reason is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await declinePaymentRun({
        runRef: run.runRef,
        reason: reason.trim(),
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'DeclinePaymentRunFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title="Decline payment run"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting && reason.trim() !== ''}
      saveLabel={submitting ? 'Declining…' : 'Decline run'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <p className="t-body-sm">
          Declining marks this run as terminal. The captured invoices stay APPROVED
          and will be picked up by the next scheduling run.
        </p>
        <label className="form-row form-row-full">
          <span className="form-label">Reason</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            placeholder="Captured in the run's audit trail"
          />
        </label>
        {error && (
          <div className="alert alert-neg" role="alert">
            {error}
          </div>
        )}
      </form>
    </SidePanel>
  );
}
